// Helpers for restaurant module pages.
import api from "@/lib/api";

export const ORDER_STATUSES = [
  "draft",
  "sent_to_kitchen",
  "in_preparation",
  "ready",
  "served",
  "paid",
  "cancelled",
];

export const TABLE_STATUSES = [
  "available",
  "occupied",
  "reserved",
  "cleaning",
  "out_of_service",
];

export const RESERVATION_STATUSES = [
  "pending",
  "confirmed",
  "arrived",
  "seated",
  "completed",
  "cancelled",
  "no_show",
];

export const TAKEAWAY_STATUSES = [
  "new",
  "confirmed",
  "preparing",
  "ready",
  "picked_up",
  "cancelled",
];

export const TABLE_STATUS_TONE = {
  available: "success",
  occupied: "warning",
  reserved: "info",
  cleaning: "neutral",
  out_of_service: "danger",
};

export const ORDER_STATUS_TONE = {
  draft: "neutral",
  sent_to_kitchen: "info",
  in_preparation: "warning",
  ready: "info",
  served: "warning",
  paid: "success",
  cancelled: "danger",
};

export const RESERVATION_STATUS_TONE = {
  pending: "warning",
  confirmed: "info",
  arrived: "info",
  seated: "success",
  completed: "neutral",
  cancelled: "danger",
  no_show: "danger",
};

export const TAKEAWAY_STATUS_TONE = {
  new: "info",
  confirmed: "info",
  preparing: "warning",
  ready: "success",
  picked_up: "neutral",
  cancelled: "danger",
};

// Lightweight wrappers (most pages just call api directly, but these are
// here to centralise the restaurant URL prefix when we add caching later).
export const restaurantApi = {
  dashboard: () => api.get("/restaurant/dashboard"),
  rooms: () => api.get("/restaurant/rooms"),
  createRoom: (payload) => api.post("/restaurant/rooms", payload),
  tables: (params = {}) => api.get("/restaurant/tables", { params }),
  createTable: (payload) => api.post("/restaurant/tables", payload),
  patchTable: (id, payload) => api.patch(`/restaurant/tables/${id}`, payload),
  deleteTable: (id) => api.delete(`/restaurant/tables/${id}`),
  categories: () => api.get("/restaurant/menu/categories"),
  createCategory: (payload) => api.post("/restaurant/menu/categories", payload),
  items: (params = {}) => api.get("/restaurant/menu/items", { params }),
  createItem: (payload) => api.post("/restaurant/menu/items", payload),
  patchItem: (id, payload) => api.patch(`/restaurant/menu/items/${id}`, payload),
  deleteItem: (id) => api.delete(`/restaurant/menu/items/${id}`),
  orders: (params = {}) => api.get("/restaurant/orders", { params }),
  order: (id) => api.get(`/restaurant/orders/${id}`),
  createOrder: (payload) => api.post("/restaurant/orders", payload),
  patchOrder: (id, payload) => api.patch(`/restaurant/orders/${id}`, payload),
  addOrderItem: (id, payload) => api.post(`/restaurant/orders/${id}/items`, payload),
  patchOrderItem: (orderId, itemId, payload) =>
    api.patch(`/restaurant/orders/${orderId}/items/${itemId}`, payload),
  deleteOrderItem: (orderId, itemId) =>
    api.delete(`/restaurant/orders/${orderId}/items/${itemId}`),
  sendToKitchen: (id) => api.post(`/restaurant/orders/${id}/send-to-kitchen`),
  // Phase 5 — paper-kitchen workflow (no KDS)
  activeOrderForTable: (tableId) => api.get(`/restaurant/tables/${tableId}/active-order`),
  kitchenUpdate: (id) => api.post(`/restaurant/orders/${id}/kitchen-update`),
  voidOrderItem: (orderId, itemId, body) =>
    api.post(`/restaurant/orders/${orderId}/items/${itemId}/void`, body),
  kitchenTickets: (id) => api.get(`/restaurant/orders/${id}/kitchen-tickets`),
  markServed: (id) => api.post(`/restaurant/orders/${id}/mark-served`),
  payOrder: (id, payload) => api.post(`/restaurant/orders/${id}/pay`, payload),
  orderPayments: (id) => api.get(`/restaurant/orders/${id}/payments`),
  moveOrder: (id, payload) => api.post(`/restaurant/orders/${id}/move`, payload),
  splitOrder: (id, payload) => api.post(`/restaurant/orders/${id}/split`, payload),
  mergeOrders: (id, payload) => api.post(`/restaurant/orders/${id}/merge`, payload),

  // QR Menu Tavoli (Foundation)
  qrMenuSettings: () => api.get("/restaurant/qr-menu/settings"),
  qrMenuPatchSettings: (payload) => api.patch("/restaurant/qr-menu/settings", payload),
  qrMenuTables: (params = {}) => api.get("/restaurant/qr-menu/tables", { params }),
  qrMenuGenerate: (payload) => api.post("/restaurant/qr-menu/generate", payload),
  qrMenuRegenerate: (qrId) => api.post(`/restaurant/qr-menu/${qrId}/regenerate`),
  qrMenuDisable: (qrId) => api.post(`/restaurant/qr-menu/${qrId}/disable`),
  qrMenuEnable: (qrId) => api.post(`/restaurant/qr-menu/${qrId}/enable`),
  qrMenuMarkPrinted: (payload) => api.post("/restaurant/qr-menu/mark-printed", payload),
  cancelOrder: (id) => api.post(`/restaurant/orders/${id}/cancel`),
  tickets: () => api.get("/restaurant/kitchen/tickets"),
  patchTicket: (id, payload) => api.patch(`/restaurant/kitchen/tickets/${id}`, payload),
  reservations: (params = {}) => api.get("/restaurant/reservations", { params }),
  createReservation: (payload) => api.post("/restaurant/reservations", payload),
  patchReservation: (id, payload) =>
    api.patch(`/restaurant/reservations/${id}`, payload),
  seatReservation: (id, payload) =>
    api.post(`/restaurant/reservations/${id}/seat`, payload),
  takeaway: (params = {}) => api.get("/restaurant/takeaway", { params }),
  patchTakeaway: (id, payload) => api.patch(`/restaurant/takeaway/${id}`, payload),
  customers: (params = {}) => api.get("/restaurant/customers", { params }),
  payments: () => api.get("/restaurant/payments"),
  // Manager Intelligence Layer — Daily Closing
  dailyClosePreview: (date) =>
    api.get("/restaurant/daily-close/preview", { params: date ? { date } : {} }),
  dailyCloseDay: (payload) => api.post("/restaurant/daily-close/close-day", payload),
  dailyCloseHistory: (params = {}) =>
    api.get("/restaurant/daily-close/history", { params }),
  dailyCloseSnapshot: (id) => api.get(`/restaurant/daily-close/${id}`),
  dailyClosePdfRegenerate: (id) =>
    api.post(`/restaurant/daily-close/${id}/regenerate`),
};

// Direct URL helper for PDF anchor (uses Authorization via fetch in onClick)
export function dailyClosePdfUrl(snapshotId) {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}/api/restaurant/daily-close/${snapshotId}/pdf`;
}

export function publicUrl(slug, segment) {
  // Returns the full external URL (origin + /r/slug/segment).
  if (typeof window === "undefined") return "";
  return `${window.location.origin}/r/${slug}/${segment}`;
}

export function tableStatusTone(status) {
  return TABLE_STATUS_TONE[status] || "neutral";
}

export function orderStatusTone(status) {
  return ORDER_STATUS_TONE[status] || "neutral";
}

export function reservationStatusTone(status) {
  return RESERVATION_STATUS_TONE[status] || "neutral";
}

export function takeawayStatusTone(status) {
  return TAKEAWAY_STATUS_TONE[status] || "neutral";
}
